import { AlloyButton } from 'cat-ecommerce-alloy';
import cx from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { UNKNOWN_ERROR_MESSAGE } from '../../../constants/commonConstants';
import {
  isEmpty,
  replaceTokens,
  replaceTokensInString,
  getCookie
} from '../../../utils';
import ExceptionFeedback from './ExceptionFeedback';
import { format } from 'date-fns';
import useHelpCenterLink from '@app/components/pages/csr-landing/hooks/useHelpCenterLink';
import endpoints from '@app/constants/endpoints';

const ExceptionFeedbackBySelector = ({
  children,
  className,
  errorCodeLabel,
  hideTitle,
  messageClassName = '',
  preventScroll,
  selector,
  showContactUs = false,
  skipTranslation,
  isClosed,
  showCloseButton,
  setClose,
  hideErrorCode,
  showDate = false,
  showReferenceId = false
}) => {
  const dispatch = useDispatch();
  const BUTTON_TYPE_SECONDARY = 'secondary';
  const { t } = useTranslation();
  const error = useSelector(selector);
  const catalogId = useSelector(state => state?.common.catalogId);
  const storeId = useSelector(state => state?.common.storeId);
  const langId = useSelector(state => state?.common.langId);
  const formattedDate = format(new Date(), "MMMM d, yyyy 'at' h:mma");
  const isHelpCenterEnabledFlag = useSelector(
    s => s.featureFlag?.PCCCustomerSupportHelpCenter
  );
  const isCatCorp = useSelector(s => s.common?.isCatCorp);
  const esiteURL = useSelector(s => s.common?.esiteURL);
  const showHelpCenterPage = isCatCorp || isHelpCenterEnabledFlag;
  const { getHelpCenterContactSupportURL } = useHelpCenterLink();
  const referenceId = getCookie('X-Cat-Pcc-Session-Id');
  const PCCContactUsPageLink = replaceTokensInString(
    endpoints.CONTACT_US_DEALER,
    catalogId,
    langId,
    storeId
  );

  const contactUsPageLink = showHelpCenterPage
    ? getHelpCenterContactSupportURL(referenceId)
    : `${esiteURL}${PCCContactUsPageLink}`;

  const {
    actions,
    code,
    message,
    showContact,
    title,
    messageParams,
    severity,
    icon,
    detailedError
  } = error || {};

  const getTitle = () => {
    if (hideTitle) {
      return undefined;
    }
    return (skipTranslation ? title : t(title)) || title;
  };

  const getDetailedError = () => {
    if (!detailedError) return '';
    return `[${t(detailedError)}]`;
  };

  const getFormattedCode = () => {
    if (!code || hideErrorCode) {
      return '';
    }
    return ` (${
      errorCodeLabel ? replaceTokensInString(t(errorCodeLabel), code) : code
    })`;
  };

  const getMessage = (m, params = [], shouldSkipTranslation = false) => {
    const selectedMessage = (shouldSkipTranslation ? m : t(m, m)) || m;
    const msg = m
      ? replaceTokens(selectedMessage, params)
      : t(UNKNOWN_ERROR_MESSAGE);
    return (
      <p
        key={msg}
        className={cx('exclude-global text-sm', messageClassName)}
      >{`${msg}${getFormattedCode()}`}</p>
    );
  };

  const handleContactUsClick = () => {
    if (showHelpCenterPage) {
      window.open(contactUsPageLink, '_blank');
    } else if (catalogId && storeId && langId) {
      const pageTitle = document.title;
      sessionStorage.setItem('referenceId', referenceId);
      sessionStorage.setItem('pageTitle', pageTitle);
      window.location.href = `CATContactUsDisplayView?catalogId=${catalogId}&langId=${langId}&storeId=${storeId}`;
    }
  };

  const getCallToAction = (
    {
      buttonText,
      buttonType = BUTTON_TYPE_SECONDARY,
      action,
      disabled = false
    },
    index
  ) => (
    <AlloyButton
      key={index}
      buttonType={buttonType}
      className="me-2"
      onClick={() => dispatch(action)}
      disabled={disabled}
    >
      {t(buttonText)}
    </AlloyButton>
  );

  return (
    ((!hideTitle && !!title) || !!message) && (
      <ExceptionFeedback
        className={className}
        icon={icon}
        preventScroll={preventScroll}
        showContact={showContact}
        isClosed={isClosed}
        setClose={setClose}
        showCloseButton={showCloseButton}
        title={`${getTitle() || '' || ''} ${getDetailedError() || '' || ''}`}
        type={severity}
      >
        {Array.isArray(message) && message.map(m => getMessage(m))}
        {typeof message === 'string' &&
          getMessage(message, messageParams, skipTranslation)}
        {children}
        <div className={'d-flex gap-2'}>
          {!isEmpty(actions) && (
            <div className="text-start my-3">
              {actions.map(getCallToAction)}
            </div>
          )}

          {showContactUs && (
            <div className="text-start my-3 my-2">
              <AlloyButton
                key="contact-us"
                buttonType="ghost"
                className=""
                onClick={handleContactUsClick}
              >
                {t('FOOTER_CONTACT_US')}
              </AlloyButton>
            </div>
          )}
        </div>
        {showReferenceId && (
          <p>
            {t('SHOPPING_CART_REFERENCE_ID')}: {referenceId}
          </p>
        )}
        {showDate && (
          <div className="mt-2 mb-n2">
            <p className="u-color--MediumGray text-xs">{formattedDate}</p>
          </div>
        )}
      </ExceptionFeedback>
    )
  );
};

ExceptionFeedbackBySelector.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]),
  className: PropTypes.string,
  errorCodeLabel: PropTypes.string,
  hideTitle: PropTypes.bool,
  messageClassName: PropTypes.string,
  preventScroll: PropTypes.bool,
  selector: PropTypes.func.isRequired,
  showContactUs: PropTypes.bool,
  isClosed: PropTypes.bool,
  setClose: PropTypes.func,
  showCloseButton: PropTypes.bool,
  skipTranslation: PropTypes.bool,
  hideErrorCode: PropTypes.bool,
  showDate: PropTypes.bool
};

export default ExceptionFeedbackBySelector;
